import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import axios from 'axios';

import { IUser, defaultValue } from 'app/shared/model/user.model';
import { IQueryParams, serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState = {
  loading: false,
  errorMessage: null as string | null,
  users: [] as readonly IUser[],
  authorities: [] as any[],
  user: defaultValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
};

const apiUrl = 'api/users';
const adminUrl = 'api/admin/users';

// Async Actions

export const getUsers = createAsyncThunk('userManagement/fetch_users', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}` : ''}`;
  return axios.get<IUser[]>(requestUrl);
});

export const getUsersAsAdmin = createAsyncThunk(
  'userManagement/fetch_users_as_admin',
  async ({ page, size, sort, query }: IQueryParams & { query?: string }, { signal }) => {
    // Timora backend exposes the admin user list as an RSQL filter endpoint that returns a
    // GenericPaginationResponse ({ data, pagination }), not the stock Spring array + X-Total-Count header.
    // `query` carries the free-text search clause built on the client (RSQL =ik= case-insensitive contains).
    const q = query ? `&q=${encodeURIComponent(query)}` : '';
    // `&sort=id,desc` is a stable tiebreaker: many users have a null createdDate, and without a unique
    // secondary key Postgres orders the ties by physical position — so editing a row (e.g. toggling
    // activated) reshuffles it off the page. The PK tiebreaker keeps the order deterministic.
    const requestUrl = `${adminUrl}/filter${sort ? `?page=${page}&size=${size}&sort=${sort}&sort=id,desc${q}` : ''}`;
    return axios.get<{ data: IUser[]; pagination: { totalItems: number } }>(requestUrl, { signal });
  },
);

export const getRoles = createAsyncThunk('userManagement/fetch_roles', async () => {
  const response = await axios.get<any[]>(`api/authorities`);
  response.data = response?.data?.map(authority => authority.name);
  return response;
});

export const getUser = createAsyncThunk(
  'userManagement/fetch_user',
  async (id: string) => {
    const requestUrl = `${adminUrl}/${id}`;
    return axios.get<IUser>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createUser = createAsyncThunk(
  'userManagement/create_user',
  async (user: IUser, thunkAPI) => {
    const result = await axios.post<IUser>(adminUrl, user);
    thunkAPI.dispatch(getUsersAsAdmin({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateUser = createAsyncThunk(
  'userManagement/update_user',
  async (user: IUser, thunkAPI) => {
    const result = await axios.put<IUser>(adminUrl, user);
    thunkAPI.dispatch(getUsersAsAdmin({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteUser = createAsyncThunk(
  'userManagement/delete_user',
  async (id: string, thunkAPI) => {
    const requestUrl = `${adminUrl}/${id}`;
    const result = await axios.delete<IUser>(requestUrl);
    thunkAPI.dispatch(getUsersAsAdmin({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// Enable/disable a user via the dedicated admin endpoint (only flips `activated`; avoids the full-DTO PUT
// whose 10-digit phone validation rejects the stored E.164 number). Caller reloads the list on success.
export const setUserStatus = createAsyncThunk(
  'userManagement/set_status',
  async ({ login, activated }: { login: string; activated: boolean }) => {
    return axios.patch<IUser>(`${adminUrl}/${login}/status`, { activated });
  },
  { serializeError: serializeAxiosError },
);

export type UserManagementState = Readonly<typeof initialState>;

export const UserManagementSlice = createSlice({
  name: 'userManagement',
  initialState: initialState as UserManagementState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getRoles.fulfilled, (state, action) => {
        state.authorities = action.payload.data;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.data;
      })
      .addCase(deleteUser.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.user = defaultValue;
      })
      .addCase(getUsersAsAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data.data;
        state.totalItems = action.payload.data.pagination.totalItems;
      })
      .addMatcher(isFulfilled(getUsers), (state, action) => {
        state.loading = false;
        state.users = action.payload.data;
        state.totalItems = Number.parseInt(action.payload.headers['x-total-count'], 10);
      })
      .addMatcher(isFulfilled(createUser, updateUser), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.user = action.payload.data;
      })
      .addMatcher(isPending(getUsers, getUsersAsAdmin, getUser), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createUser, updateUser, deleteUser, setUserStatus), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isFulfilled(setUserStatus), state => {
        state.updating = false;
        state.updateSuccess = true;
      })
      .addMatcher(
        isRejected(getUsers, getUsersAsAdmin, getUser, getRoles, createUser, updateUser, deleteUser, setUserStatus),
        (state, action) => {
          if (action.meta?.aborted) return; // superseded by a newer request — ignore
          state.loading = false;
          state.updating = false;
          state.updateSuccess = false;
          state.errorMessage = action.error.message!;
        },
      );
  },
});

export const { reset } = UserManagementSlice.actions;

// Reducer
export default UserManagementSlice.reducer;
